import React from "react";
import { Row, Col } from "react-bootstrap";
import { FormField } from "../FormField/FormField";
import "./FormSections.css";

/* Groups the backend-driven flat field list into titled section cards.
   Routing is keyword-based with a fallback bucket so no field is ever dropped. */

// Inputs removed from the form (values auto-injected in form.service.js so the
// positional CSV contract to the engine is unchanged). Co-borrower fields: the
// engine keeps its co-borrower capability, but the portal always prices as a
// single borrower on the primary score.
const HIDDEN = [
  "1stmortgage_interest_rate",
  "1stmortgage_term",
  "1stmortgage_loan_age",
  "number_of_borrowers",
  "co_borrower_credit_score_at_origination",
];

// Optional helper text under specific fields
const HELPERS = {
  "2ndmortgage_amount_requested": "Leave blank to price the maximum eligible amount.",
  "2ndmortgage_amount": "Leave blank to price the maximum eligible amount.",
  "1stmortgage_amount": "Enter '0' if you're looking for a first lien loan.",
  combined_annual_income: "Enter the monthly figure — we annualize it for DTI.",
  dti_pre2nd: "Borrower DTI before this loan; exclude any payoffs from the 2nd.",
  origination_date:
    "Backdates the quote to that day's rate sheet (internal only — brokers always price as of today). Leave blank for today.",
};

// Display-label overrides (v2 portal only). The DB labels are shared with the
// prod modelportal form, so renaming happens here rather than at the source.
const LABELS = {
  origination_date: "Pricing Date",
  borrower_credit_score_at_origination: "Qualifying Credit Score",
  "2ndmortgage_amount_requested": "Loan Amount Requested",
  "2ndmortgage_amount": "Loan Amount Requested",
  "2ndmortgage_draw_term": "Draw Term Requested (in months)",
  "2ndmortgage_loan_term": "Requested Loan Term",
  loan_purpose: "Loan Purpose",
};

const SECTIONS = [
  {
    title: "Product & terms",
    match: (s) =>
      /product|doc_type|amount_requested|2ndmortgage_amount|home_value|draw_term|loan_term|origination|purpose|1stmortgage/.test(
        s
      ),
  },
  {
    title: "Property",
    match: (s) => /zip|zcode|_state|property_type|number_of_units|occupancy/.test(s),
  },
  {
    title: "Borrower",
    match: (s) => /borrower|credit_score|first_name|last_name/.test(s),
  },
  { title: "Income & DTI", match: (s) => /income|dti/.test(s) },
];

export function FormSections({ fields, midSlot }) {
  const visible = (fields || []).filter((f) => !HIDDEN.includes(f.field_id));

  const buckets = SECTIONS.map(() => []);
  const extra = [];
  const internalOnly = [];
  visible.forEach((f) => {
    // Fields flagged hidden=1 are served only to internal users. Keep them in
    // one section at the BOTTOM so the shared fields sit in the same spots on
    // internal and external views (sales reps walk brokers through the form).
    if (f.hidden == 1) {
      internalOnly.push(f);
      return;
    }
    const id = (f.field_id || "").toLowerCase();
    const idx = SECTIONS.findIndex((sec) => sec.match(id));
    if (idx >= 0) buckets[idx].push(f);
    else extra.push(f);
  });

  const renderField = (f) => {
    const field = LABELS[f.field_id]
      ? { ...f, field_label: LABELS[f.field_id] }
      : f;
    return (
      <Col key={f.field_id} xs={12} md={6}>
        <FormField field={field} />
        {HELPERS[f.field_id] && (
          <small className="bf-field-help">{HELPERS[f.field_id]}</small>
        )}
      </Col>
    );
  };

  const renderSection = (title, list, key) =>
    list.length ? (
      <section className="bf-formsection" key={key}>
        <h6 className="bf-section-title">{title}</h6>
        <Row>{list.map(renderField)}</Row>
      </section>
    ) : null;

  return (
    <>
      {SECTIONS.map((sec, i) => renderSection(sec.title, buckets[i], sec.title))}
      {renderSection("Additional details", extra, "more")}
      {/* slot between the shared sections and Internal only — the Encompass
          pull card lives here (owner: "between Income & DTI and Internal only") */}
      {midSlot || null}
      {renderSection("Internal only", internalOnly, "internal")}
    </>
  );
}

export default FormSections;
